import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Button, Icon } from '@reservamos/elements';
import PropTypes from 'prop-types';
import closeMark from 'images/icons/close-mark.png';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useGrowthBookFeatureValue from 'components/GrowthBookProvider/useGrowthBookFeatureValue';
import isReserhubUnifiedAccountEnabled from 'utils/isReserhubUnifiedAccountEnabled';

/**
 * Onboarding component.
 * Renders a modal overlay with a video tutorial and descriptive text.
 * @param {object} props - handleOnClose.
 * @param {function} props.handleOnClose - Function to close the modal.
 * @returns {JSX.Element} A modal component with video and descriptive content.
 */
const Onboarding = ({ handleOnClose }) => {
  const whitelabelEnvs = useWhitelabelEnvs();
  const { onBoarding, reserhub } = whitelabelEnvs;
  const onboardingFeature = useGrowthBookFeatureValue('onboarding');
  const unifiedAccountFeature = useGrowthBookFeatureValue('unified_account');
  const { t } = useTranslation();

  const accountEnabled = isReserhubUnifiedAccountEnabled(reserhub?.account, {
    unifiedAccountFeature,
  });
  if (!onboardingFeature || !onBoarding || !accountEnabled) return null;

  const { videoUrl } = onBoarding;

  return (
    <div
      className="p-2 md:p-0 fixed inset-0 flex items-center justify-center bg-gray-400 bg-opacity-70 z-50"
      style={{ backdropFilter: 'blur(8px)' }}
    >
      <div className="relative w-full max-w-xl rounded-2xl shadow-2xl bg-white overflow-hidden">
        <div className="bg-gradient-to-br from-primary-regular/10 to-primary-regular/50 relative">
          <button
            className="absolute top-4 right-4 z-50 cursor-pointer focus:outline-none"
            aria-label="Close"
            onClick={handleOnClose}
          >
            <Icon type={closeMark} size="S" />
          </button>
          <div>
            <video
              src={videoUrl}
              autoPlay
              muted
              loop
              className="w-full h-auto rounded-lg"
              data-testid="onboarding-video"
            />
          </div>
        </div>
        <div
          className="bg-white/60 border text-center border-white flex flex-col gap-2 md:gap-4 p-4 md:p-6 rounded-2xl -mt-8 relative z-30 shadow-md"
          style={{ backdropFilter: 'blur(8px)' }}
        >
          <Text weight="semibold" size="XL" mobileSize="L">
            {t('common:onboarding.title')}
          </Text>
          <Text weight="regular" size="M" mobileSize="S">
            {t('common:onboarding.description')}
          </Text>
          <Button
            text={t('common:onboarding.button')}
            variant="primary"
            color="white"
            padding="XL"
            mobileSize="S"
            mobilePadding="L"
            borderRadius="M"
            fullWidth
            onClick={() => {
              handleOnClose();
              window.location.href = reserhub.account.loginUrl;
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Onboarding;

Onboarding.propTypes = {
  handleOnClose: PropTypes.func.isRequired,
};
